import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";

import { useStableRest } from "../../../../utils/use-stable-rest";
import { LIST_TOOLBAR_SEARCH_CLEAR_LABEL, LIST_TOOLBAR_SEARCH_PLACEHOLDER } from "../../messages";
import { LIST_TOOLBAR_SEARCH_WIDTHS } from "./search.constants";
import type {
  ListToolbarSearchProps,
  ListToolbarSearchViewModel,
  ListToolbarSearchWidthPreset,
} from "./types-search";

const isWidthPreset = (width: string): width is ListToolbarSearchWidthPreset =>
  (LIST_TOOLBAR_SEARCH_WIDTHS as readonly string[]).includes(width);

/** ViewModel do ListToolbar.Search: texto digitado local (com debounce opcional) sincronizado com o `value`; sem JSX. */
export function useListToolbarSearch({
  value,
  onChange,
  placeholder = LIST_TOOLBAR_SEARCH_PLACEHOLDER,
  debounceMs = 0,
  maxLength,
  clearable = false,
  width = "default",
  size,
  "aria-label": ariaLabel,
  testId,
  className,
  style,
  ...props
}: ListToolbarSearchProps): ListToolbarSearchViewModel {
  const rest = useStableRest(props);
  const [draft, setDraft] = useState(value);
  const preset = isWidthPreset(width);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mudança externa do valor (ex.: limpar filtros) substitui o texto local.
  useEffect(() => {
    // Também cancela a emissão pendente: sem isso o "abc" digitado antes de o pai limpar o valor dispararia depois.
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setDraft(value);
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return useMemo(
    () => ({
      draft,
      placeholder,
      maxLength,
      showClear: clearable && draft !== "",
      clearLabel: LIST_TOOLBAR_SEARCH_CLEAR_LABEL,
      onClear: () => {
        if (timer.current) clearTimeout(timer.current);
        setDraft("");
        onChange("");
      },
      ariaLabel: ariaLabel ?? placeholder,
      testId,
      clearTestId: testId ? `${testId}-clear` : undefined,
      className,
      style: preset ? style : { width, ...style },
      dataWidth: preset ? width : undefined,
      dataSize: size,
      rest,
      onInput: (event: ChangeEvent<HTMLInputElement>) => {
        const next = event.target.value;
        setDraft(next);
        if (timer.current) clearTimeout(timer.current);
        if (debounceMs > 0) timer.current = setTimeout(() => onChange(next), debounceMs);
        else onChange(next);
      },
    }),
    [
      draft,
      placeholder,
      maxLength,
      clearable,
      ariaLabel,
      testId,
      className,
      style,
      width,
      size,
      preset,
      rest,
      debounceMs,
      onChange,
    ],
  );
}
